<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  CUSTOM_LAUNCHER_STYLE_ID,
  WEB_WIDGET_LAUNCHER_STYLES,
} from 'dashboard/constants/webWidgetLauncherStyles';
import {
  INBOX_AVATAR_ACCEPT,
  validateInboxAvatarFile,
} from 'dashboard/helper/inboxAvatarUpload';

const props = defineProps({
  modelValue: {
    type: String,
    default: '1',
  },
  widgetColor: {
    type: String,
    default: '#009CE0',
  },
  customIconPreview: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:customIconPreview',
  'customIconSelected',
]);

const { t } = useI18n();
const iconFileInputRef = ref(null);

const selectBubbleStyle = styleId => {
  emit('update:modelValue', styleId);
};

const selectCustomBubbleStyle = () => {
  if (!props.customIconPreview) return;
  emit('update:modelValue', CUSTOM_LAUNCHER_STYLE_ID);
};

const openCustomIconPicker = () => {
  iconFileInputRef.value?.click();
};

const onCustomIconChange = event => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  const validation = validateInboxAvatarFile(file);
  if (!validation.valid) {
    useAlert(
      t(
        `INBOX_MGMT.WIDGET_CUSTOMIZATION_SECTIONS.AVATAR_UPLOAD.${validation.errorKey}`
      )
    );
    return;
  }

  if (props.customIconPreview?.startsWith('blob:')) {
    URL.revokeObjectURL(props.customIconPreview);
  }

  emit('update:customIconPreview', URL.createObjectURL(file));
  emit('customIconSelected', file);
  emit('update:modelValue', CUSTOM_LAUNCHER_STYLE_ID);
};

const customIconButtonClass = isSelected =>
  isSelected
    ? 'size-14 border-n-brand'
    : 'size-12 border-transparent opacity-80 hover:opacity-100';
</script>

<template>
  <div class="w-full">
    <p class="mb-2 text-sm font-medium text-n-slate-12">
      {{ $t('INBOX_MGMT.ADD.WIDGET_STYLE.LABEL') }}
    </p>
    <div class="flex flex-wrap items-end gap-3">
      <button
        v-for="style in WEB_WIDGET_LAUNCHER_STYLES"
        :key="style.id"
        type="button"
        class="flex shrink-0 items-center justify-center rounded-full border-2 transition-all duration-200"
        :class="
          modelValue === style.id
            ? 'size-14 border-n-brand text-white'
            : 'size-12 border-transparent text-white opacity-80 hover:opacity-100'
        "
        :style="{ backgroundColor: widgetColor }"
        @click="selectBubbleStyle(style.id)"
      >
        <Icon
          :icon="style.icon"
          :class="modelValue === style.id ? 'size-6' : 'size-5'"
        />
      </button>
      <button
        v-if="customIconPreview"
        type="button"
        class="flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 p-0 transition-all duration-200"
        :class="customIconButtonClass(modelValue === CUSTOM_LAUNCHER_STYLE_ID)"
        :style="{ backgroundColor: widgetColor }"
        @click="selectCustomBubbleStyle"
      >
        <img
          :src="customIconPreview"
          alt=""
          class="size-full object-contain p-1"
        />
      </button>
      <button
        type="button"
        class="flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-dashed transition-colors"
        :class="
          modelValue === CUSTOM_LAUNCHER_STYLE_ID && customIconPreview
            ? 'border-n-brand bg-n-alpha-2 text-n-brand'
            : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:border-n-brand hover:text-n-brand'
        "
        :aria-label="$t('INBOX_MGMT.ADD.WIDGET_STYLE.CUSTOM_ICON_LABEL')"
        @click="openCustomIconPicker"
      >
        <Icon icon="i-lucide-plus" class="size-5" />
      </button>
      <input
        ref="iconFileInputRef"
        type="file"
        :accept="INBOX_AVATAR_ACCEPT"
        class="hidden"
        @change="onCustomIconChange"
      />
    </div>
    <p class="mt-2 flex items-center gap-1 text-label-small text-n-slate-11">
      <span>{{ $t('INBOX_MGMT.ADD.WIDGET_STYLE.CUSTOM_ICON_LABEL') }}</span>
      <Icon
        v-tooltip.top="
          $t('INBOX_MGMT.ADD.WIDGET_STYLE.CUSTOM_ICON_UPLOAD_HINT')
        "
        icon="i-lucide-info"
        class="size-3.5 shrink-0 text-n-slate-10 cursor-help"
      />
    </p>
  </div>
</template>
